import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import useIsMobile from "../hooks/useIsMobile";

// images
import logo from "../images/common/logo.png";
import logoWhite from "../images/common/logo_white.png";
import iconTel from "../images/common/icon/fixed-btn/tel.webp";
import iconPhone from "../images/common/icon/fixed-btn/phone.webp";

// --- 1. 데이터 정의 (Data Structure) ---
// 구조는 단순화하되, 렌더링 시 원본 HTML 구조를 그대로 재현합니다.

interface NavItem {
    label: string;
    link?: string; // 1차 메뉴 클릭 시 이동할 링크 (없으면 null)
    children?: MenuItem[];
}

interface MenuItem {
    label: string;
    link?: string;
    // 3차 메뉴(Depth 3)가 있는 경우
    subChildren?: { label: string; link: string }[];
}

const NAV_ITEMS: NavItem[] = [
    {
        label: "회사소개",
        children: [
            { label: "우수조달컨설팅 소개", link: "/company/about" },
            { label: "인증성공업체", link: "/company/certified-company" },
            { label: "유튜브 소개", link: "/company/youtube" },
            { label: "컨설턴트 소개", link: "/company/consultant" },
            { label: "찾아오시는 길", link: "/company/contact" },
        ],
    },
    {
        label: "인증 분야",
        children: [
            {
                label: "조달인증",
                subChildren: [
                    { label: "우수조달제품인증", link: "/certification/procurement" },
                    { label: "혁신제품인증", link: "/certification/procurement?1" },
                    { label: "다수공급자계약(MAS)", link: "/certification/procurement?2" },
                    { label: "품질보증조달물품인증", link: "/certification/procurement?3" },
                    { label: "벤처나라 등록", link: "/certification/procurement?4" },
                    { label: "우수조달공동상표인증", link: "/certification/procurement?5" },
                    { label: "디지털서비스인증", link: "/certification/procurement?6" },
                    { label: "상용소프트웨어 제3자단가계약", link: "/certification/procurement?7" },
                    { label: "G-PASS", link: "/certification/procurement?8" },
                ],
            },
            {
                label: "품질인증",
                subChildren: [
                    { label: "성능인증(EPC)", link: "/certification/quality-cert" },
                    { label: "K마크", link: "/certification/quality-cert?1" },
                    { label: "환경표지인증", link: "/certification/quality-cert?2" },
                    { label: "KC인증", link: "/certification/quality-cert?3" },
                    { label: "GR인증", link: "/certification/quality-cert?4" },
                    { label: "GS인증", link: "/certification/quality-cert?5" },
                    { label: "단체표준인증", link: "/certification/quality-cert?6" },
                    { label: "고효율에너지", link: "/certification/quality-cert?7" },
                    { label: "S마크인증", link: "/certification/quality-cert?8" },
                    { label: "Q마크인증", link: "/certification/quality-cert?9" },
                    { label: "ISO9001", link: "/certification/quality-cert?10" },
                    { label: "ISO14001", link: "/certification/quality-cert?11" },
                ],
            },
            {
                label: "기술인증",
                subChildren: [
                    { label: "신제품인증(NEP)", link: "/certification/tech-cert" },
                    { label: "신기술인증(NET)", link: "/certification/tech-cert?1" },
                    { label: "KS인증", link: "/certification/tech-cert?2" },
                    { label: "녹색기술인증", link: "/certification/tech-cert?3" },
                ],
            },
            {
                label: "경영인증",
                subChildren: [
                    { label: "벤처기업", link: "/certification/management-cert" },
                    { label: "이노비즈", link: "/certification/management-cert?1" },
                    { label: "기업부설연구소", link: "/certification/management-cert?2" },
                    { label: "메인비즈", link: "/certification/management-cert?3" },
                ],
            },
        ],
    },
    {
        label: "원스톱 서비스",
        children: [
            { label: "서비스 내용", link: "/one-stop/service-info" },
            { label: "세부 내용", link: "/one-stop/details" },
            { label: "서비스 인력", link: "/one-stop/service-staff" },
        ],
    },
    {
        label: "고객지원",
        children: [
            { label: "공지사항", link: "/customer/notice" },
            { label: "정보공유", link: "/customer/information" },
            { label: "채용정보", link: "/customer/careers" },
            { label: "자주묻는 질문", link: "/customer/faq" },
            { label: "문의하기", link: "/customer/contact" },
        ],
    },
];

// --- 2. Styled Components (원본 유지) ---
// 디자인 변경을 방지하기 위해 원본 코드를 그대로 사용합니다.

const Container = styled.header`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    height: 80px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    & * {
        color: #fff;
    }
    &.on {
        border-bottom: 1px solid var(--subColor05);
        background-color: #fff;
    }
    &.on * {
        color: var(--subColor01);
    }
    &.on .btn_menu_wrap {
        & .btn_menu {
            color: var(--subColor01);
        }
    }
    & .logo_normal {
        display: none;
    }
    & .logo_white {
        display: block;
    }
    &.on .logo_normal {
        display: block;
    }
    &.on .logo_white {
        display: none;
    }
    & .tel_wrap {
        line-height: 1em;
        border: 1px solid #fff;
        border-radius: 9999px;
        padding: 12px 24px;
        color: #fff;
        & * {
            color: #fff;
        }
    }
    &.on .tel_wrap {
        border-color: var(--pointColor01);
        background-color: var(--pointColor01);
    }
    & .right_item_wrap {
        height: 80px;
    }
    .category_dep02 {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 200px;
        height: 50px;
        .icon-plus {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 14px;
            height: 14px;
            font-size: 14px;
            border: 1px solid #e4e4e4;
            margin-left: 8px;
            color: var(--pointColor01);
        }
    }
    .dep3-items-container {
        display: none;
        &.on {
            display: block;
        }
        .dep3-item {
            text-align: center;
            font-size: 13px;
            padding: 4px 0px;
            color: #707070;
            &:hover {
                text-decoration: underline;
                color: #0525c9;
            }
        }
    }
    .btn-site-map {
        .btn_menu {
            font-size: 26px;
        }
    }
    .scrolling-wrap {
        position: fixed;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 100%;
        overflow-y: scroll;
    }
    .site-map-container {
        width: 100%;
        height: auto;
        background-color: #f5f5f5;
        .site-map {
            width: 1440px;
            padding: 80px 0px;
            margin-left: auto;
            margin-right: auto;
            .category:not(:last-of-type) {
                border-bottom: 1px solid var(--subColor04);
                .dep3-item {
                    min-width: 205px;
                    color: var(--subColor03);
                    &:hover {
                        text-decoration: underline;
                    }
                }
            }
        }
    }
    /* pc */
    @media screen and (min-width: 1280px) {
        &.hover {
            height: auto;
            padding-bottom: 20px;
            background-color: #fff;
            & .logo_normal {
                display: block;
            }
            & .logo_white {
                display: none;
            }
            & .item_dep02_wrap {
                display: block;
            }
            & .tel_wrap {
                border-color: var(--pointColor01);
                background-color: var(--pointColor01);
            }
        }
        &.hover * {
            color: var(--subColor01);
        }
        &.hover .tel_wrap {
            color: #fff;
        }
        &.hover .tel_wrap * {
            color: #fff;
        }
    }

    /* 저해상도 */
    @media screen and (max-width: 1500px) {
        * {
            font-size: 15px;
        }
        .category_dep01,
        .category_dep02 {
            width: 150px;
        }
        .tel_wrap {
            white-space: nowrap;
        }
        .scrolling-wrap {
            display: none;
        }
        .btn-site-map {
            display: none;
        }
    }

    /* 모바일 */
    @media screen and (max-width: 1280px) {
        height: 50px;
        .btn_menu_wrap {
            .btn_menu {
                font-size: 26px;
            }
        }
        &.open {
            height: auto;
        }
        &.open .category_wrap {
            overflow: hidden scroll;
            display: block;
            position: absolute;
            top: 50px;
            left: 0px;
            width: 100%;
            height: calc(100vh - 50px);
            padding-top: 24px;
            padding-bottom: 80px;
            background-color: #fff;
            .category_dep01,
            .category_dep02 {
                justify-content: flex-start;
                width: 100%;
                height: 45px;
                text-align: left;
                padding: 0px 16px;
            }
            .category_dep01 {
                font-weight: 600;
                &.on + .item_dep02_wrap {
                    display: block;
                }
                &.on > .icon_arrow {
                    transform: rotate(180deg);
                }
            }
            .category_dep02 {
                height: 40px;
                padding: 0px 24px;
                font-size: 13px;
                background-color: #f5f5f5;
            }
        }
        .right_item_wrap {
            height: 50px;
            .btn_menu_wrap {
                width: 50px;
                height: 50px;
            }
        }
        .dep3-items-container .dep3-item {
            height: 35px;
            line-height: 35px;
            text-align: left;
            padding: 0px 24px;
        }
        .category_dep02 {
            display: flex;
            justify-content: space-between !important;
            width: 100%;
            .icon-plus {
                display: none;
            }
        }
    }
`;
const CategoryWrap = styled.ul`
    display: flex;
    & .item_dep02_wrap {
        display: none;
    }
    @media screen and (max-width: 1280px) {
        display: none;
    }
`;
const Logo = styled(Link)`
    display: flex;
    justify-content: center;
    align-items: center;
    width: auto;
    height: 80px;
    @media screen and (max-width: 1280px) {
        height: 50px;
    }
`;
const CategoryItemDep01 = styled.p`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 200px;
    height: 80px;
`;
const MenuIcon = styled.button`
    display: block;
    width: 80px;
    height: 80px;
    .btn_menu {
        font-size: 26px;
    }
    @media screen and (min-width: 1280px) {
        display: none;
        .btn_menu {
            font-size: 30px;
        }
    }
`;

export default function Header() {
    const location = useLocation();
    const pathname = location.pathname;

    // React State로 원본의 DOM 조작 로직을 대체
    // 0: default, 1: active (open)
    const [headerActive, setHeaderActive] = useState<number>(0);
    const [categoryIndex, setCategoryIndex] = useState<number | string>("");
    const [isHover, setIsHover] = useState(false);

    // Depth3 Toggle State (각 메뉴별 열림/닫힘 상태)
    const [activeDep3, setActiveDep3] = useState<{ [key: string]: boolean }>({});

    const [isVisibleSiteMap, setIsVisibleSiteMap] = useState(false);

    // Hooks
    const is1500Under = useIsMobile({ responsive: 1500 });
    const is1280Under = useIsMobile({ responsive: 1280 });

    // --- Handlers ---

    const headerIndex = () => {
        setHeaderActive((prev) => (prev === 0 ? 1 : 0));
    };

    const categoryItemClick = (index: number) => {
        setCategoryIndex((prev) => (prev === index ? "" : index));
    };

    // Depth3 메뉴 토글 핸들러 (uniqueKey 사용)
    const toggleDep3 = (uniqueKey: string) => {
        setActiveDep3((prev) => {
            // 다른 메뉴는 닫고 현재 메뉴만 토글 (Accordion 방식)
            // 만약 다중 열림을 원하면 { ...prev, [uniqueKey]: !prev[uniqueKey] } 로 변경
            const isOpened = prev[uniqueKey];
            return isOpened ? {} : { [uniqueKey]: true };
        });
    };

    // 사이트맵 제어
    const handleSiteMapOpen = () => setIsVisibleSiteMap(true);
    const handleSiteMapClose = () => setIsVisibleSiteMap(false);

    // --- Effects (원본 로직 복원) ---

    // Scroll Lock
    useEffect(() => {
        if (isVisibleSiteMap || headerActive === 1) {
            document.body.classList.add("disable-scroll");
        } else {
            document.body.classList.remove("disable-scroll");
        }
    }, [isVisibleSiteMap, headerActive]);

    // 반응형 대응
    useEffect(() => {
        if (is1500Under) setIsVisibleSiteMap(false);
    }, [is1500Under]);

    useEffect(() => {
        if (!is1280Under) setHeaderActive(0);
    }, [is1280Under]);

    // 경로 변경 시 초기화
    useEffect(() => {
        window.scrollTo(0, 0);
        setIsHover(false);
        setIsVisibleSiteMap(false);
        setHeaderActive(0);
    }, [pathname, location]);

    return (
        <div className="fixed top-0 left-0 w-full z-[9980]">
            {/* 전화 */}
            {headerActive !== 1 && (
                <div className="px-36 xl:px-80 flex items-center xl:py-0 py-4 justify-end xl:h-64 bg-[linear-gradient(86deg,#302D2C_35%,#E95E02_100%)] text-white">
                    <a
                        href="tel:031-234-2870"
                        className="flex flex-wrap items-center gap-4 xl:gap-16 pr-32 mr-32 border-r border-white/50"
                    >
                        <img className="size-16 xl:size-32" src={iconTel} alt="tel icon" />
                        <p className="text-sm xl:text-2xl">전화 상담</p>
                        <strong className="text-base xl:text-2xl md:w-auto w-full">031-234-2870</strong>
                    </a>
                    <a href="tel:010-4611-2870" className="flex flex-wrap items-center gap-4 xl:gap-16">
                        <img className="size-16 xl:size-32" src={iconPhone} alt="phone icon" />
                        <p className="text-sm xl:text-2xl">주말/휴일</p>
                        <strong className="text-base xl:text-2xl md:w-auto w-full">010-4611-2870</strong>
                    </a>
                </div>
            )}

            <Container
                id="header"
                className={`${headerActive === 1 ? "open" : ""} ${isHover ? "hover" : ""} on`}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
            >
                <Logo className="xl:ml-40 ml-16" to="/">
                    <img className="xl:h-48 h-34 logo_normal" src={logo} alt="logo" />
                    <img className="xl:h-48 h-34 logo_white" src={logoWhite} alt="logo" />
                </Logo>

                {/* GNB Navigation */}
                <CategoryWrap className="category_wrap">
                    {NAV_ITEMS.map((item, depth1Index) => (
                        <li key={depth1Index}>
                            <CategoryItemDep01
                                className={`${categoryIndex === depth1Index ? "on" : ""} category_dep01 xl:text-20`}
                                onClick={() => categoryItemClick(depth1Index)}
                            >
                                {item.label}
                                <i className="icon_arrow xl:hidden inline ml-auto text-15 xi-angle-down-min"></i>
                            </CategoryItemDep01>

                            {/* 2Depth Wrapper */}
                            <ul className={`${categoryIndex === depth1Index ? "on" : ""} item_dep02_wrap`}>
                                {item.children?.map((subItem, depth2Index) => {
                                    // 각 Depth3 메뉴의 고유 키 생성 (예: "1-0" -> 인증분야 첫번째 메뉴)
                                    const uniqueKey = `${depth1Index}-${depth2Index}`;
                                    const isDep3Open = activeDep3[uniqueKey];

                                    return (
                                        <li key={depth2Index}>
                                            {subItem.subChildren ? (
                                                /* Depth 3가 있는 경우 (버튼 + 하위 목록) */
                                                <>
                                                    <button
                                                        onClick={() => toggleDep3(uniqueKey)}
                                                        className="category_dep02"
                                                    >
                                                        {subItem.label}
                                                        <i
                                                            className={`icon-plus xi-${isDep3Open ? "minus" : "plus"}-min`}
                                                        ></i>
                                                        <i
                                                            className={`xl:hidden xi-angle-${isDep3Open ? "up" : "down"}-min`}
                                                        ></i>
                                                    </button>
                                                    <ul className={`${isDep3Open ? "on" : ""} dep3-items-container`}>
                                                        {subItem.subChildren.map((child, depth3Index) => (
                                                            <li key={depth3Index}>
                                                                <Link className="dep3-item" to={child.link}>
                                                                    {child.label}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </>
                                            ) : (
                                                /* Depth 3가 없는 경우 (단일 링크) */
                                                <Link className="category_dep02" to={subItem.link || "#"}>
                                                    {subItem.label}
                                                </Link>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </li>
                    ))}
                </CategoryWrap>

                {/* Right Icons */}
                <div className="right_item_wrap flex items-center xl:gap-20 xl:pr-40">
                    {/* 원본의 MenuIcon 및 btn_menu_wrap 클래스 유지 */}
                    <MenuIcon onClick={headerIndex} className="btn_menu_wrap">
                        <i className="btn_menu xi-align-right text-white text-30"></i>
                    </MenuIcon>
                    <button onClick={handleSiteMapOpen} className="btn-site-map xl:block hidden">
                        <i className="btn_menu xi-align-right text-white text-30"></i>
                    </button>
                </div>

                {/* Site Map Overlay */}
                {isVisibleSiteMap && (
                    <div className="scrolling-wrap site-map-wrap">
                        <div className="site-map-container">
                            <div className="flex justify-between items-center">
                                <Logo className="xl:ml-40 ml-16" to="/">
                                    <img className="xl:h-48 h-34 logo_normal" src={logo} alt="logo" />
                                </Logo>
                                <button onClick={handleSiteMapClose} className="pr-40">
                                    <i className="btn_menu xi-close text-black xl:text-30 text-26"></i>
                                </button>
                            </div>
                            <div className="site-map">
                                <div>
                                    <ul>
                                        {NAV_ITEMS.map((item, i) => (
                                            <li key={i} className="category pb-60 mb-60">
                                                <strong className="leading-1em text-32 font-bold">{item.label}</strong>

                                                {/* '인증분야'처럼 하위 메뉴가 많은 경우 flex-col, 아니면 flex-row */}
                                                <ul
                                                    className={`mt-48 ${item.children?.some((c) => c.subChildren) ? "flex flex-col gap-56" : "flex gap-100"}`}
                                                >
                                                    {item.children?.map((sub, j) => (
                                                        <li key={j} className={sub.subChildren ? "flex gap-120" : ""}>
                                                            {sub.subChildren ? (
                                                                /* 2 Depth Title */
                                                                <strong className="text-20 font-semibold">
                                                                    {sub.label}
                                                                </strong>
                                                            ) : (
                                                                /* 2 Depth Link */
                                                                <Link
                                                                    to={sub.link || "#"}
                                                                    className="text-20 font-semibold"
                                                                >
                                                                    {sub.label}
                                                                </Link>
                                                            )}

                                                            {/* 3 Depth Items */}
                                                            {sub.subChildren && (
                                                                <ul className="flex flex-wrap gap-y-40">
                                                                    {sub.subChildren.map((child, k) => (
                                                                        <li key={k}>
                                                                            <Link
                                                                                to={child.link}
                                                                                className="dep3-item text-18"
                                                                            >
                                                                                {child.label}
                                                                            </Link>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Container>
        </div>
    );
}
